import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  TextField,
  MenuItem,
  Pagination,
  Alert,
  List,
  ListItem,
  CircularProgress,
  Card,
  CardContent,
  Tooltip,
} from '@mui/material';
import {
  Login as LoginIcon,
  Logout as LogoutIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Cancel as CancelIcon,
  Visibility as ViewIcon,
  Upload as UploadIcon,
  Download as DownloadIcon,
  Settings as SettingsIcon,
  Security as SecurityIcon,
  History as HistoryIcon,
  FileDownload as ExportIcon,
  Public as WebIcon,
  AdminPanelSettings as AdminIcon,
  Code as ApiIcon,
  Extension as PluginIcon,
} from '@mui/icons-material';
import axios from 'axios';

const ActivityHistory = () => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({
    eventType: '',
    timeframe: 'monthly',
    source: '',
  });
  const [stats, setStats] = useState(null);

  const eventTypes = [
    { value: '', label: 'All Events' },
    { value: 'login', label: 'Login' },
    { value: 'logout', label: 'Logout' },
    { value: 'observation_created', label: 'Observation Created' },
    { value: 'observation_updated', label: 'Observation Updated' },
    { value: 'observation_deleted', label: 'Observation Deleted' },
    { value: 'observation_viewed', label: 'Observation Viewed' },
    { value: 'file_uploaded', label: 'File Uploaded' },
    { value: 'file_downloaded', label: 'File Downloaded' },
    { value: 'observatory_updated', label: 'Observatory Updated' },
  ];

  const timeframes = [
    { value: 'daily', label: 'Last 24 Hours' },
    { value: 'weekly', label: 'Last Week' },
    { value: 'monthly', label: 'Last Month' },
  ];

  const sources = [
    { value: '', label: 'All Sources' },
    { value: 'web_ui', label: 'Web Interface' },
    { value: 'admin_ui', label: 'Admin Interface' },
    { value: 'api', label: 'API' },
    { value: 'plugin', label: 'NINA Plugin' },
  ];

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchActivities();
    fetchActivityStats();
  }, [page, filters]);

  const fetchActivities = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams({
        page: page.toString(),
        limit: '20',
      });

      if (filters.eventType) params.append('event_type', filters.eventType);
      if (filters.timeframe) params.append('timeframe', filters.timeframe);
      if (filters.source) params.append('source', filters.source);

      const response = await axios.get(`/api/v1/userActivity/me?${params}`);

      if (response.data.success) {
        const data = response.data.data;
        setActivities(data.activities || []);
        if (data.pagination) {
          setTotalPages(data.pagination.pages || 1);
        }
      } else {
        setError('Failed to fetch activity data');
      }
    } catch (err) {
      console.error('Error fetching activities:', err);
      setError('Unable to load activity history');
    } finally {
      setLoading(false);
    }
  };

  const fetchActivityStats = async () => {
    try {
      const response = await axios.get('/api/v1/userActivity/me/stats');
      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching activity stats:', err);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPage(1); // Reset to first page when filtering
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const exportActivities = async (format = 'csv') => {
    try {
      const response = await axios.get(`/api/v1/userActivity/export?format=${format}`, {
        responseType: format === 'csv' ? 'blob' : 'json'
      });

      if (format === 'csv') {
        const blob = new Blob([response.data], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `activity-history-${new Date().toISOString().split('T')[0]}.csv`;
        link.click();
        window.URL.revokeObjectURL(url);
      } else {
        const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `activity-history-${new Date().toISOString().split('T')[0]}.json`;
        link.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (err) {
      console.error('Error exporting activities:', err);
      setError('Failed to export activity data');
    }
  };

  const getActivityIcon = (eventType) => {
    switch (eventType) {
      case 'login': return <LoginIcon color="success" />;
      case 'login_failed':
      case 'failed_login':
      case 'login_failure': return <LoginIcon color="warning" />;
      case 'logout': return <LogoutIcon color="action" />;
      case 'observation_created': return <AddIcon color="primary" />;
      case 'observation_updated': return <EditIcon color="info" />;
      case 'observation_deleted': return <DeleteIcon color="error" />;
      case 'observation_cancelled':
      case 'observation_canceled': return <CancelIcon color="warning" />;
      case 'observation_viewed': return <ViewIcon color="action" />;
      case 'file_uploaded': return <UploadIcon color="success" />;
      case 'file_downloaded': return <DownloadIcon color="info" />;
      case 'observatory_updated':
      case 'observatory_created': return <SettingsIcon color="primary" />;
      case 'access_denied': return <SecurityIcon color="warning" />;
      default: return <HistoryIcon color="action" />;
    }
  };

  const formatDate = (timestamp) => {
    const d = new Date(timestamp);
    if (Number.isNaN(d.getTime())) return '—';
    // Display in UTC (the system's canonical timezone) so entries don't shift with the viewer's locale.
    const mo = (d.getUTCMonth() + 1).toString().padStart(2, '0');
    const da = d.getUTCDate().toString().padStart(2, '0');
    const yr = d.getUTCFullYear();
    const hh = d.getUTCHours().toString().padStart(2, '0');
    const mm = d.getUTCMinutes().toString().padStart(2, '0');
    const ss = d.getUTCSeconds().toString().padStart(2, '0');
    return `${mo}-${da}-${yr} ${hh}:${mm}:${ss} UTC`;
  };

  const renderSourceIcon = (source) => {
    const sx = { fontSize: 14, color: 'text.secondary' };
    switch (source) {
      case 'web_ui': return <Tooltip title="Web Interface" arrow><WebIcon sx={sx} /></Tooltip>;
      case 'admin_ui': return <Tooltip title="Admin Interface" arrow><AdminIcon sx={sx} /></Tooltip>;
      case 'api': return <Tooltip title="API" arrow><ApiIcon sx={sx} /></Tooltip>;
      case 'plugin': return <Tooltip title="NINA Plugin" arrow><PluginIcon sx={sx} /></Tooltip>;
      default: return source ? <Tooltip title={source} arrow><HistoryIcon sx={sx} /></Tooltip> : null;
    }
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Activity History
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Track all your actions and interactions with the system
        </Typography>
      </Box>

      {/* Activity Stats — compact tile row. Each tile shows a small
          uppercase caption above a single-row big-number value. */}
      {stats && (() => {
        const total = stats.summary?.reduce((acc, item) => acc + (item.count || 0), 0) || 0;
        const logins = stats.loginStats?.filter(i => i._id?.event_type === 'login')
          .reduce((acc, i) => acc + (i.count || 0), 0) || 0;
        const filesUploaded = stats.recentFileActivity?.filter(i => i.event_type === 'file_uploaded').length || 0;
        const obsCreated = stats.recentObservatoryActivity?.filter(i => i.event_type === 'observation_created').length || 0;
        const tile = (label, value, color) => (
          <Card variant="outlined" sx={{ flex: '1 1 0', minWidth: 110 }}>
            <CardContent sx={{ py: 0.75, px: 1, '&:last-child': { pb: 0.75 } }}>
              <Typography sx={{ fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: 0.4, color: 'text.secondary' }}>
                {label}
              </Typography>
              <Typography sx={{ fontSize: '1.1rem', fontWeight: 600, color: color || 'text.primary', lineHeight: 1.2 }}>
                {value}
              </Typography>
            </CardContent>
          </Card>
        );
        return (
          <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
            {tile('Total Activities', total)}
            {tile('Logins (30 days)', logins, 'success.main')}
            {tile('Files Uploaded', filesUploaded, 'info.main')}
            {tile('Observations Created', obsCreated, 'primary.main')}
          </Box>
        );
      })()}

      {/* Filters and Export */}
      <Paper sx={{ p: 1.5, mb: 2 }}>
        {(() => {
          const fieldSx = {
            '& .MuiInputLabel-root': { fontSize: '0.75rem' },
            '& .MuiSelect-select': { fontSize: '0.75rem', py: 0.5 },
            '& .MuiInputBase-input': { fontSize: '0.75rem' },
          };
          const menuItemSx = { fontSize: '0.75rem', py: 0.5 };
          return (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
              <TextField
                select size="small" label="Event Type"
                value={filters.eventType}
                onChange={(e) => handleFilterChange('eventType', e.target.value)}
                sx={{ minWidth: 160, ...fieldSx }}
              >
                {eventTypes.map((option) => (
                  <MenuItem key={option.value} value={option.value} sx={menuItemSx}>{option.label}</MenuItem>
                ))}
              </TextField>
              <TextField
                select size="small" label="Time Period"
                value={filters.timeframe}
                onChange={(e) => handleFilterChange('timeframe', e.target.value)}
                sx={{ minWidth: 140, ...fieldSx }}
              >
                {timeframes.map((option) => (
                  <MenuItem key={option.value} value={option.value} sx={menuItemSx}>{option.label}</MenuItem>
                ))}
              </TextField>
              <TextField
                select size="small" label="Source"
                value={filters.source}
                onChange={(e) => handleFilterChange('source', e.target.value)}
                sx={{ minWidth: 140, ...fieldSx }}
              >
                {sources.map((option) => (
                  <MenuItem key={option.value} value={option.value} sx={menuItemSx}>{option.label}</MenuItem>
                ))}
              </TextField>
              <Box sx={{ display: 'flex', gap: 1, flexShrink: 0, ml: 'auto' }}>
                <Button
                  variant="outlined"
                  startIcon={<ExportIcon sx={{ fontSize: 14 }} />}
                  onClick={() => exportActivities('csv')}
                  size="small"
                  sx={{ fontSize: '0.7rem', whiteSpace: 'nowrap', textTransform: 'none', py: 0.25 }}
                >
                  Export CSV
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<ExportIcon sx={{ fontSize: 14 }} />}
                  onClick={() => exportActivities('json')}
                  size="small"
                  sx={{ fontSize: '0.7rem', whiteSpace: 'nowrap', textTransform: 'none', py: 0.25 }}
                >
                  Export JSON
                </Button>
              </Box>
            </Box>
          );
        })()}
      </Paper>

      {/* Activities List */}
      <Paper sx={{ p: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : activities.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1" color="text.secondary">
              No activities found for the selected filters
            </Typography>
          </Box>
        ) : (
          <>
            <List dense disablePadding>
              {activities.map((activity, index) => (
                <ListItem
                  key={activity._id || index}
                  disableGutters
                  sx={{
                    py: 0.5,
                    px: 1,
                    borderBottom: index < activities.length - 1 ? 1 : 0,
                    borderColor: 'divider',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <Tooltip title={activity.event_type.replace(/_/g, ' ')} arrow>
                    <Box sx={{ display: 'inline-flex', flexShrink: 0 }}>
                      {React.cloneElement(getActivityIcon(activity.event_type), { fontSize: 'small' })}
                    </Box>
                  </Tooltip>
                  <Typography
                    component="span"
                    sx={{ fontFamily: 'monospace', fontSize: '0.75rem', color: 'text.secondary', whiteSpace: 'nowrap', flexShrink: 0 }}
                  >
                    {formatDate(activity.timestamp)}
                  </Typography>
                  <Typography
                    component="span"
                    sx={{ fontSize: '0.85rem', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  >
                    {activity.description || activity.event_type.replace(/_/g, ' ')}
                  </Typography>
                  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, flexShrink: 0 }}>
                    {renderSourceIcon(activity.source)}
                  </Box>
                </ListItem>
              ))}
            </List>

            {/* Pagination */}
            {totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={handlePageChange}
                  color="primary"
                />
              </Box>
            )}
          </>
        )}
      </Paper>
    </Container>
  );
};

export default ActivityHistory;